import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, X, Loader2, CalendarX } from "lucide-react";

const typeMap = {
  justificada: { label: "Justificada", cls: "bg-emerald-500/15 text-emerald-400" },
  injustificada: { label: "Injustificada", cls: "bg-red-500/15 text-red-400" },
  ferias: { label: "Férias", cls: "bg-sky-500/15 text-sky-400" },
  doenca: { label: "Doença", cls: "bg-amber-500/15 text-amber-400" },
};

export default function FaltasPage() {
  const [list, setList] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ user_id: "", date: "", type: "justificada", reason: "" });

  const load = () =>
    Promise.all([api.get("/absences"), api.get("/users")])
      .then(([a, u]) => {
        setList(a.data);
        setEmployees(u.data);
      })
      .finally(() => setLoading(false));
  useEffect(() => {
    load();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/absences", form);
      toast.success("Falta registada");
      setModal(false);
      setForm({ user_id: "", date: "", type: "justificada", reason: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (a) => {
    if (!window.confirm("Eliminar este registo de falta?")) return;
    await api.delete(`/absences/${a.id}`);
    toast.success("Registo eliminado");
    load();
  };

  return (
    <div data-testid="faltas-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Registo de Faltas</h1>
          <p className="text-muted-foreground">{list.length} registo(s).</p>
        </div>
        <button
          data-testid="add-falta-button"
          onClick={() => setModal(true)}
          className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold hover:bg-[#F5B05E] transition-colors"
        >
          <Plus className="w-4 h-4" /> Registar falta
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-7 h-7 text-primary animate-spin" />
        </div>
      ) : list.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-12 text-center">
          <CalendarX className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">Sem faltas registadas.</p>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground border-b border-border">
                <tr>
                  <th className="px-5 py-3 font-medium">Funcionário</th>
                  <th className="px-5 py-3 font-medium">Data</th>
                  <th className="px-5 py-3 font-medium">Tipo</th>
                  <th className="px-5 py-3 font-medium">Motivo</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {list.map((a) => {
                  const t = typeMap[a.type] || typeMap.justificada;
                  return (
                    <tr key={a.id} data-testid={`falta-row-${a.id}`} className="border-b border-border/50 last:border-0">
                      <td className="px-5 py-3 font-medium">{a.user_name}</td>
                      <td className="px-5 py-3 font-data">{a.date}</td>
                      <td className="px-5 py-3">
                        <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${t.cls}`}>{t.label}</span>
                      </td>
                      <td className="px-5 py-3 text-muted-foreground">{a.reason || "—"}</td>
                      <td className="px-5 py-3 text-right">
                        <button
                          data-testid={`delete-falta-${a.id}`}
                          onClick={() => remove(a)}
                          className="p-2 rounded-lg text-red-400 hover:bg-red-500/10 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={() => setModal(false)} />
          <form onSubmit={save} data-testid="falta-form" className="relative bg-card border border-border rounded-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display text-xl font-bold">Registar falta</h2>
              <button type="button" onClick={() => setModal(false)}>
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1.5">Funcionário</label>
                <select
                  data-testid="falta-user-select"
                  required
                  value={form.user_id}
                  onChange={(e) => setForm({ ...form, user_id: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-lg bg-input border border-border focus:border-primary outline-none"
                >
                  <option value="">— Selecionar —</option>
                  {employees.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Data</label>
                <input
                  data-testid="falta-date"
                  type="date"
                  required
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-lg bg-input border border-border focus:border-primary outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Tipo</label>
                <select
                  data-testid="falta-type-select"
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-lg bg-input border border-border focus:border-primary outline-none"
                >
                  <option value="justificada">Justificada</option>
                  <option value="injustificada">Injustificada</option>
                  <option value="ferias">Férias</option>
                  <option value="doenca">Doença</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Motivo</label>
                <textarea
                  data-testid="falta-reason"
                  rows={3}
                  value={form.reason}
                  onChange={(e) => setForm({ ...form, reason: e.target.value })}
                  className="w-full px-3 py-2.5 rounded-lg bg-input border border-border focus:border-primary outline-none resize-none"
                />
              </div>
            </div>
            <button data-testid="save-falta-button" disabled={saving} className="w-full mt-6 py-3 rounded-full bg-primary text-primary-foreground font-semibold hover:bg-[#F5B05E] transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} Guardar
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
